import React, { useState } from 'react';
import { NavLink } from 'react-router';
import { Icons } from '@shared/constants';
import { useAuth } from '../../context/AuthContext';
import { useFeatureFlags } from '../../context/RemoteConfigContext';
import { useDemoMode } from '../../demo/DemoContext';
import { logError } from '@/lib/log';

export const Sidebar: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const { routePrefix } = useDemoMode();
  const [avatarError, setAvatarError] = useState(false);

  // Pause holds the third primary slot; Grow lives in the secondary list and
  // the three spending tools are Pause tabs.
  const navItems = [
    { to: `${routePrefix}/`, icon: Icons.Home, label: 'Home' },
    { to: `${routePrefix}/reflect`, icon: Icons.Activity, label: 'Reflect' },
    { to: `${routePrefix}/pause`, icon: Icons.Pause, label: 'Pause' },
    { to: `${routePrefix}/story`, icon: Icons.Compass, label: 'Your story' },
  ];

  // Secondary navigation items
  const secondaryItems = [
    { to: `${routePrefix}/grow`, icon: Icons.Leaf, label: 'Grow' },
    { to: `${routePrefix}/journal`, icon: Icons.BookOpen, label: 'Journal' },
    ...(isFeatureEnabled('goals')
      ? [{ to: `${routePrefix}/goals`, icon: Icons.Target, label: 'Goals' }]
      : []),
    ...(isFeatureEnabled('debtManager')
      ? [{ to: `${routePrefix}/debt`, icon: Icons.TrendingDown, label: 'Debt plan' }]
      : []),
    { to: `${routePrefix}/connect`, icon: Icons.Link, label: 'Accounts' },
    { to: `${routePrefix}/profile`, icon: Icons.User, label: 'Profile' },
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      logError(error, { context: 'logout' });
    }
  };

  return (
    <aside className="hidden lg:flex flex-col w-64 bg-white/80 backdrop-blur-xl border-r border-mist/20 h-screen sticky top-0">
      {/* Logo */}
      <div className="p-6 border-b border-mist/10">
        <img
          src="/assets/logos/enough_wordmark_green.png"
          alt="Enough Logo"
          className="h-16 object-contain"
        />
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        <div className="mb-4">
          <p className="text-xs font-semibold text-growth uppercase tracking-wider px-3 mb-2">
            Your journey
          </p>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === `${routePrefix}/`}
                className={({ isActive }) =>
                  `w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                    isActive
                      ? 'bg-foliage/10 text-growth border border-foliage/20'
                      : 'text-growth hover:bg-sand hover:text-growth'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5px]' : 'stroke-2'}`} />
                    <span className={`font-medium text-sm ${isActive ? 'font-semibold' : ''}`}>
                      {item.label}
                    </span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>

        {/* Secondary Navigation */}
        <div className="pt-4 border-t border-mist/10">
          <p className="text-xs font-semibold text-growth uppercase tracking-wider px-3 mb-2">
            Details
          </p>
          {secondaryItems.map((item) => {
            const Icon = item.icon;
            const isProfile = item.to === '/profile';
            const showAvatar = isProfile && currentUser?.photoURL && !avatarError;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-foliage/10 text-growth border border-foliage/20'
                      : 'text-growth hover:bg-sand'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {showAvatar ? (
                      <img
                        src={currentUser.photoURL!}
                        alt={currentUser.displayName || 'Profile'}
                        className="w-5 h-5 rounded-full object-cover"
                        onError={() => setAvatarError(true)}
                      />
                    ) : (
                      <Icon className={`w-5 h-5 stroke-2 ${isActive ? '' : 'opacity-70'}`} />
                    )}
                    <span className="font-medium text-sm">{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Footer with Logout */}
      <div className="p-4 border-t border-mist/10 space-y-2">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-enough-wildfire hover:bg-enough-wildfire/10 transition-all duration-200"
        >
          <Icons.LogOut className="w-5 h-5 stroke-2" />
          <span className="font-medium text-sm">Log out</span>
        </button>
      </div>

    </aside>
  );
};
